import { OnSendDeleteConfirmation } from '~/features/events';
import { sendDeleteConfirmation, sendErrorAlert, sendSuccessAlert } from '~/features/utils';
import {
  ALERT_VIEW_STATE,
  ATTESTATION_STATE,
  CONFIRMATION_VIEW_STATE,
} from '~/features/xstateSystem';
import {
  AttestationResponseEvent,
  AttestationsResponseEvent,
  EditAttestation,
  JSONFormResponseEvent,
  OnSetProviderIdEvent,
  PDFResponseEvent,
} from './events';
import { Context, Scope } from './types';

export const setJSONForm = (scope: Scope): Partial<Context> => {
  const { output } = scope.event as JSONFormResponseEvent;
  return {
    loading: false,
    formUi: output.jsonForm.jsonFormUi,
    form: output.jsonForm.jsonForm,
    attestationFormId: output.id,
    jsonFormId: output.jsonForm.id,
    providerType: 'Physician',
    defaultData: undefined,
    idToEdit: undefined,
  };
};

export const handleModal = (scope: Scope): Partial<Context> => {
  return {
    modal: !scope.context.modal,
  };
};

export const setProviderId = (scope: Scope): Partial<Context> => {
  const event = scope.event as OnSetProviderIdEvent;
  return {
    providerId: event.payload.providerId,
  };
};

export const loadAttestations = (scope: Scope): Partial<Context> => {
  const { output } = scope.event as AttestationsResponseEvent;
  return {
    attestations: output,
    loading: false,
  };
};

export const addAttestation = (scope: Scope): Partial<Context> => {
  const { output } = scope.event as AttestationResponseEvent;
  const attestations = scope.context.attestations;
  attestations.push(output);
  return {
    attestations,
  };
};

export const loadPdf = (scope: Scope) => {
  const { output } = scope.event as PDFResponseEvent;

  const blob = new Blob([output], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);

  // MCV-1286: the completed attestation opens in a new tab. Many browsers block
  // window.open() by default, so tell the user what to expect and recover gracefully
  // if the popup was suppressed.
  const popup = window.open(url, '_blank');
  if (!popup || popup.closed || typeof popup.closed === 'undefined') {
    scope.system.get(ALERT_VIEW_STATE)?.send(
      sendErrorAlert({
        text: 'Pop-up blocked',
        subText:
          'Your browser blocked the completed form from opening. Enable pop-ups for this site, then re-submit to view it.',
      })
    );
  } else {
    scope.system.get(ALERT_VIEW_STATE)?.send(
      sendSuccessAlert({
        text: 'Attestation submitted',
        subText:
          'Your completed form is opening in a new tab. Allow pop-ups for this site if you don’t see it.',
      })
    );
  }

  setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
};

export const sendSaveProgressSuccess = (scope: Scope) => {
  scope.system.get(ALERT_VIEW_STATE)?.send(
    sendSuccessAlert({
      text: 'Progress saved',
      subText: 'Your attestation progress has been saved.',
    })
  );
};

export const sendSaveProgressError = (scope: Scope) => {
  scope.system.get(ALERT_VIEW_STATE)?.send({
    type: 'ON_SHOW_ALERT',
    payload: {
      text: 'Unable to save progress',
      subText: 'Your attestation progress was not saved. Please try again.',
      type: 'error',
      timeout: 15000,
    },
  });
};

export const editAttestation = (scope: Scope): Partial<Context> => {
  const { payload } = scope.event as EditAttestation;
  const attestation = scope.context.attestations.find(
    (attestation) => attestation.id == payload.id
  );
  if (!attestation) {
    return {};
  }
  return {
    formUi: attestation.formInstance.jsonForm?.jsonFormUi || {},
    form: attestation.formInstance.jsonForm?.jsonForm || {},
    attestationFormId: attestation.attestationFormId,
    jsonFormId: attestation.formInstance.jsonFormId,
    providerType: 'Physician',
    defaultData: attestation.formInstance.formData,
    idToEdit: attestation.id,
  };
};

export const resetIdToDelete = (): Partial<Context> => {
  return {
    idToEdit: undefined,
  };
};

export const loadingTrue = (): Partial<Context> => {
  return {
    loading: true,
  };
};

export const loadingFalse = (): Partial<Context> => {
  return {
    loading: false,
  };
};

export const resetForm = (): Partial<Context> => {
  return {
    formUi: {},
    form: {},
    defaultData: undefined,
    idToEdit: undefined,
  };
};

export const openDeleteConfirmation = (scope: Scope) => {
  const event = scope.event as OnSendDeleteConfirmation;

  const onConfirm = (id: number) => {
    scope.system.get(ATTESTATION_STATE).send({ type: 'ON_DELETE', payload: { id } });
  };
  scope.system
    .get(CONFIRMATION_VIEW_STATE)
    .send(sendDeleteConfirmation(() => onConfirm(event.payload.id)));
};
